const toFiniteNumber = (value) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
};

const roundNumber = (value, digits = 2) => {
    const number = toFiniteNumber(value);
    if (number == null) {
        return null;
    }
    return Number(number.toFixed(digits));
};

const normaliseLivePositions = (livePosition) => {
    if (!livePosition) {
        return [];
    }
    if (Array.isArray(livePosition)) {
        return livePosition.filter((row) => row?.symbol);
    }
    return livePosition.symbol ? [livePosition] : [];
};

const mergeSummaryMetrics = (summary, livePositions) => {
    const next = { ...(summary || {}) };
    const positions = normaliseLivePositions(livePositions);
    if (!positions.length) {
        return next;
    }

    const preferJournalPnl = next.pnlTelemetryStale === true || next.pnlSource === 'journal';
    const currentOpenPnl = toFiniteNumber(next.openPnl);
    const currentNetPnl = toFiniteNumber(next.netPnl);
    const currentTcaOpenPnl = toFiniteNumber(next.tcaOpenPnl);
    const currentTcaNetPnl = toFiniteNumber(next.tcaNetPnl);
    const tcaOpenAdjustment =
        currentTcaOpenPnl != null && currentOpenPnl != null ? currentTcaOpenPnl - currentOpenPnl : null;
    const tcaNetAdjustment =
        currentTcaNetPnl != null && currentNetPnl != null ? currentTcaNetPnl - currentNetPnl : null;

    const summedOpenPnl = positions.reduce(
        (sum, position) => sum + (toFiniteNumber(position?.openPnl) || 0),
        0,
    );
    const summedNetPnl = positions.reduce(
        (sum, position) => sum + (toFiniteNumber(position?.netPnl) || 0),
        0,
    );
    const nextOpenPnl = roundNumber(summedOpenPnl);
    // When journal owns realized P&L (stale telemetry), keep dayPnl and recompute
    // net from realized + live open MTM instead of trusting per-leg netPnl sums
    // that may still embed yesterday's closed-session totals.
    const journalDayPnl = toFiniteNumber(next.dayPnl);
    const nextNetPnl = preferJournalPnl && journalDayPnl != null && nextOpenPnl != null
        ? roundNumber(journalDayPnl + nextOpenPnl)
        : roundNumber(summedNetPnl);

    if (nextOpenPnl != null) {
        next.openPnl = nextOpenPnl;
        if (tcaOpenAdjustment != null) {
            next.tcaOpenPnl = roundNumber(nextOpenPnl + tcaOpenAdjustment);
        }
    }
    if (nextNetPnl != null) {
        next.netPnl = nextNetPnl;
        if (tcaNetAdjustment != null) {
            next.tcaNetPnl = roundNumber(nextNetPnl + tcaNetAdjustment);
        }
    }
    if (positions.length === 1 && positions[0]?.currentPrice != null) {
        next.optionPrice = roundNumber(positions[0].currentPrice);
    }

    return next;
};

const mergePositionRow = (position, livePosition) => {
    if (!position || position.symbol !== livePosition?.symbol || String(position.status || '').toLowerCase() !== 'open') {
        return position;
    }

    const liveUniverse = livePosition.universe ? String(livePosition.universe).toUpperCase() : null;
    const positionUniverse = position.universe ? String(position.universe).toUpperCase() : null;
    if (liveUniverse && positionUniverse && liveUniverse !== positionUniverse) {
        return position;
    }

    const meta = { ...(position.meta || {}) };
    if (livePosition.currentPrice != null) {
        meta.optionLtp = roundNumber(livePosition.currentPrice);
    }
    if (livePosition.lastUpdatedAt) {
        meta.ltpTimestamp = livePosition.lastUpdatedAt;
    }

    return {
        ...position,
        price: roundNumber(livePosition.currentPrice) ?? position.price,
        pnl: roundNumber(livePosition.openPnl) ?? position.pnl,
        mtm: roundNumber(livePosition.openPnl) ?? position.mtm ?? position.pnl,
        netPnl: roundNumber(livePosition.netPnl) ?? position.netPnl,
        meta,
    };
};

const mergeTradeHistoryRow = (trade, livePosition, stateCommentary) => {
    if (!trade || trade.symbol !== livePosition?.symbol || String(trade.status || '').toLowerCase() !== 'open') {
        return trade;
    }

    const liveUniverse = livePosition.universe ? String(livePosition.universe).toUpperCase() : null;
    const tradeUniverse = trade.universe ? String(trade.universe).toUpperCase() : null;
    if (liveUniverse && tradeUniverse && liveUniverse !== tradeUniverse) {
        return trade;
    }

    const liveSlot = livePosition.slot || livePosition.tag || null;
    const tradeSlot = trade.tag || trade.slot || null;
    if (liveSlot && tradeSlot && liveSlot !== tradeSlot) {
        return trade;
    }

    return {
        ...trade,
        quantity: toFiniteNumber(livePosition.qty) ?? trade.quantity,
        currentPrice: roundNumber(livePosition.currentPrice) ?? trade.currentPrice,
        openPnl: roundNumber(livePosition.openPnl) ?? trade.openPnl,
        pnl: roundNumber(livePosition.openPnl) ?? trade.pnl,
        netPnl: roundNumber(livePosition.netPnl) ?? trade.netPnl,
        lastUpdatedAt: livePosition.lastUpdatedAt || trade.lastUpdatedAt,
        currentCommentary: stateCommentary || trade.currentCommentary,
    };
};

export const mergeLivePositionOverlay = (state, livePosition) => {
    const livePositions = normaliseLivePositions(livePosition);
    if (!state || !livePositions.length) {
        return state;
    }

    const summary = mergeSummaryMetrics(state.summary, livePositions);
    const stateCommentary = summary.stateCommentary || state.summary?.stateCommentary || null;
    const activity = Array.isArray(state.activity)
        ? state.activity.map((entry) => {
            const positions = Array.isArray(entry?.positions) ? entry.positions : [];
            const matchingPositions = livePositions.filter((liveRow) => positions.some(
                (position) => position?.symbol === liveRow.symbol
                    && String(position?.status || '').toLowerCase() === 'open',
            ));
            if (!matchingPositions.length) {
                return entry;
            }

            return {
                ...entry,
                summary: mergeSummaryMetrics(entry.summary, matchingPositions),
                positions: positions.map((position) => {
                    const liveRow = matchingPositions.find((row) => row.symbol === position.symbol);
                    return liveRow ? mergePositionRow(position, liveRow) : position;
                }),
            };
        })
        : state.activity;
    const tradeHistory = Array.isArray(state.tradeHistory)
        ? state.tradeHistory.map(
            (trade) => livePositions.reduce(
                (current, liveRow) => mergeTradeHistoryRow(current, liveRow, stateCommentary),
                trade,
            ),
        )
        : state.tradeHistory;

    return {
        ...state,
        summary,
        activity,
        tradeHistory,
    };
};
